import React from 'react';
import { Link } from 'react-router-dom';
import { FaLock, FaSpinner, FaShieldAlt, FaCode, FaGlobe } from 'react-icons/fa';
import PasswordInput from '../../../components/common/PasswordInput';
import logo from '../../../assets/arvix/arvix_logo.png';

const AdminLogin = ({ credentials, onChange, onSubmit, isAuthenticating }) => (
  <div className="min-h-screen flex">
    {/* Brand panel */}
    <div className="hidden lg:flex lg:w-1/2 bg-designer-dark relative overflow-hidden items-center justify-center p-12">
      <div className="absolute inset-0 bg-gradient-to-br from-designer-primary/20 via-transparent to-transparent" />
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-designer-primary/10 blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-red-600/10 blur-3xl" />

      <div className="relative z-10 max-w-md text-center">
        <img src={logo} alt="Arvix Creative" className="h-14 w-auto mx-auto mb-8 brightness-0 invert" />
        <h2 className="text-3xl font-display font-bold text-white mb-3">
          Admin Console
        </h2>
        <p className="text-white/60 text-sm leading-relaxed">
          Manage course enrollments, student payments, contact messages, and reviews, all in one place.
        </p>
        <div className="mt-10 flex items-center justify-center gap-6 text-white/40 text-xs">
          <div className="flex items-center gap-2">
            <FaShieldAlt className="w-3.5 h-3.5" />
            <span>Secure access</span>
          </div>
        </div>
      </div>
    </div>

    {/* Login form */}
    <div className="flex-1 flex flex-col px-4 py-12 bg-[#F4F6F9]">
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <img src={logo} alt="Arvix Creative" className="h-10 w-auto mx-auto mb-4" />
            <h1 className="text-2xl font-display font-bold text-designer-dark">Admin Login</h1>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200/80">
            <div className="hidden lg:block mb-8">
              <h1 className="text-2xl font-display font-bold text-designer-dark mb-1">Welcome back</h1>
              <p className="text-sm text-gray-500">Sign in to your admin account</p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-designer-dark mb-1.5">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  value={credentials.username}
                  onChange={(e) => onChange({ ...credentials, username: e.target.value })}
                  placeholder="Enter username"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-designer-dark placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-designer-primary/30 focus:border-designer-primary/40 transition-all text-sm"
                  disabled={isAuthenticating}
                  autoComplete="username"
                />
              </div>
              <PasswordInput
                id="password"
                label="Password"
                labelClassName="block text-sm font-medium text-designer-dark mb-1.5"
                value={credentials.password}
                onChange={(e) => onChange({ ...credentials, password: e.target.value })}
                placeholder="Enter password"
                inputClassName="w-full pl-4 pr-11 py-3 bg-gray-50 border border-gray-200 rounded-xl text-designer-dark placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-designer-primary/30 focus:border-designer-primary/40 transition-all text-sm"
                disabled={isAuthenticating}
                autoComplete="current-password"
                withIcon={false}
              />
              <button
                type="submit"
                disabled={isAuthenticating}
                className="w-full bg-designer-primary hover:bg-red-600 text-white font-semibold py-3.5 px-6 rounded-xl transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm shadow-lg shadow-designer-primary/20"
              >
                {isAuthenticating ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <FaLock className="w-3.5 h-3.5" />
                    Sign in
                  </>
                )}
              </button>
            </form>
          </div>

          <div className="mt-6 flex items-center justify-center gap-3 text-xs text-gray-400">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 font-semibold text-gray-500 hover:text-designer-primary transition-colors"
            >
              <FaGlobe className="w-3.5 h-3.5" aria-hidden="true" />
              Visit the site
            </Link>
            <span aria-hidden="true" className="text-gray-300">&middot;</span>
            <span>Arvix Creative &middot; Authorized personnel only</span>
          </div>
        </div>
      </div>
      <a
        href="https://junaidul.pro.bd/codejborg"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Developed by CodeJBorg: visit developer website"
        className="group mt-6 flex items-center justify-center gap-1.5 font-mono text-[11px] text-gray-400 transition-colors hover:text-designer-dark"
      >
        <FaCode className="size-3.5" aria-hidden="true" />
        <span>Developed by</span>
        <span className="font-semibold text-gray-500 transition-colors group-hover:text-designer-dark">
          <span className="text-designer-primary">&lt;</span>CodeJBorg<span className="text-designer-primary"> /&gt;</span>
        </span>
      </a>
    </div>
  </div>
);

export default AdminLogin;
